export type FleetMode = "own_fleet_only" | "third_party_only";

export type CoverageType = "own_fleet" | "preferred_carrier" | "spot_carrier";

export type FleetModeRestrictedPage =
  | "dispatch"
  | "fleet"
  | "exceptions"
  | "quotes"
  | "shipments"
  | "tracking"
  | "carriers"
  | "templates"
  | "addresses"
  | "integrations";

export const DEFAULT_FLEET_MODE: FleetMode = "third_party_only";

export const FLEET_MODE_LABELS: Record<FleetMode, string> = {
  third_party_only: "Carrier Network",
  own_fleet_only: "Own Fleet TMS",
};

export const FLEET_MODE_SETTINGS_COPY: Record<
  FleetMode,
  { title: string; description: string; bullets: string[] }
> = {
  third_party_only: {
    title: "Carrier Network keeps Shippeek focused on rates, booking, and tracking.",
    description: "Best for teams shipping through UPS, FedEx, USPS, Estes, regional LTL, and global carriers.",
    bullets: [
      "Quotes, shipments, tracking, carrier credentials, addresses, and templates stay in navigation.",
      "Fleet dispatch, truck registry, exception queue, and telematics setup are hidden to reduce clutter.",
      "This is the default mode for most new Shippeek workspaces.",
    ],
  },
  own_fleet_only: {
    title: "Own Fleet TMS keeps the workspace centered on dispatch operations.",
    description: "Best for companies managing their own trucks, drivers, trailers, and telematics providers.",
    bullets: [
      "Dispatch, Fleet Registry, Exceptions, reports, and fleet integrations stay in navigation.",
      "Generic carrier rating, booking, tracking, and carrier credential pages are hidden.",
      "Carrier tendering remains blocked for new actions while this mode is active.",
    ],
  },
};

const OWN_FLEET_GROUPS = new Set(["dispatch", "fleet", "exceptions", "analytics", "account"]);
const CARRIER_NETWORK_GROUPS = new Set([
  "quotes",
  "shipments",
  "tracking",
  "carriers",
  "templates",
  "addresses",
  "analytics",
  "account",
]);

export function isFleetMode(value: unknown): value is FleetMode {
  return value === "own_fleet_only" || value === "third_party_only";
}

export function normalizeFleetMode(value: unknown): FleetMode {
  return isFleetMode(value) ? value : DEFAULT_FLEET_MODE;
}

export function isNavGroupVisibleForFleetMode(fleetMode: FleetMode, groupId: string): boolean {
  if (fleetMode === "own_fleet_only") return OWN_FLEET_GROUPS.has(groupId);
  return CARRIER_NETWORK_GROUPS.has(groupId);
}

export function isNavItemVisibleForFleetMode(
  fleetMode: FleetMode,
  groupId: string,
  itemPath: string,
): boolean {
  if (!isNavGroupVisibleForFleetMode(fleetMode, groupId)) return false;

  if (groupId === "account" && itemPath === "/account/integrations") {
    return true;
  }

  return true;
}

export function isCoverageAllowedForFleetMode(fleetMode: FleetMode, coverageType: CoverageType): boolean {
  if (fleetMode === "own_fleet_only") return coverageType === "own_fleet";
  return coverageType !== "own_fleet";
}

export function getFleetModeCapabilityMessage(
  fleetMode: FleetMode,
  capability: "assign_fleet" | "tender_carrier",
): string {
  if (capability === "assign_fleet") {
    return fleetMode === "third_party_only"
      ? "Fleet assignments are disabled while Carrier Network mode is active."
      : "Fleet assignments are available.";
  }

  return fleetMode === "own_fleet_only"
    ? "Carrier tendering is disabled while Own Fleet TMS mode is active."
    : "Carrier tendering is available.";
}

export function getFleetModePageRestriction(page: FleetModeRestrictedPage, fleetMode: FleetMode): {
  title: string;
  description: string;
} | null {
  const ownFleetOnlyPages = new Set<FleetModeRestrictedPage>(["dispatch", "fleet", "exceptions"]);
  const carrierNetworkOnlyPages = new Set<FleetModeRestrictedPage>([
    "quotes",
    "shipments",
    "tracking",
    "carriers",
    "templates",
    "addresses",
  ]);

  if (fleetMode === "third_party_only" && ownFleetOnlyPages.has(page)) {
    return {
      title: "Own Fleet TMS pages are hidden in Carrier Network mode.",
      description: "Switch to Own Fleet TMS mode to manage dispatch, trucks, drivers, trailers, exceptions, and telematics integrations.",
    };
  }

  if (fleetMode === "own_fleet_only" && carrierNetworkOnlyPages.has(page)) {
    return {
      title: "Carrier Network pages are hidden in Own Fleet TMS mode.",
      description: "Switch to Carrier Network mode to manage carrier credentials, rate shopping, booking, tracking, addresses, and templates.",
    };
  }

  return null;
}
